/* eslint-disable */
import React, {
    useState, useEffect, useRef, useCallback,
} from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    MessagesSquare,
    SendToBack,
    Briefcase,
    Code,
    ScrollText,
    SendHorizontal
} from 'lucide-react';

const { PUBLIC_URL } = process.env;
const WEBHOOK_URL = 'https://n8n.ajithjoseph.com/webhook/79e19a27-89b2-45db-ae8e-9f3668bb3bb2/chat';

const QUICK_ACTIONS = [
    {
        label: "Send an email",
        icon: Briefcase,
        topic: "Contact Ajith joseph"
    },
    {
        label: "Recent Projects",
        icon: Briefcase,
        topic: "Tell me about your projects"
    },
    {
        label: "Tech Stack",
        icon: Code,
        topic: "What tech stack do you use?"
    },
    {
        label: "Available for work?",
        icon: ScrollText,
        topic: "Are you available for work?"
    }
];

const ChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [showTyping, setShowTyping] = useState(false);
    const [showFollowUp, setShowFollowUp] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: uuidv4(),
            role: 'assistant',
            content: "Hi! I'm Ajith's AI assistant. Ask me about his work, skills or availability.",
        },
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId] = useState(uuidv4());
    const messagesEndRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const markGreetingSeen = () => {
        try { localStorage.setItem('chatGreetingSeen', '1'); } catch (e) { /* storage unavailable */ }
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
            setShowNotification(false);
            markGreetingSeen();
        }
    }, [messages, isOpen, scrollToBottom]);

    // Greeting bubble: shown once per browser, not again after it is opened or dismissed.
    useEffect(() => {
        let seen = true;
        try { seen = !!localStorage.getItem('chatGreetingSeen'); } catch (e) { /* storage unavailable */ }
        if (isOpen || seen) return undefined;
        const timer = setTimeout(() => setShowNotification(true), 5000);
        return () => clearTimeout(timer);
    }, [isOpen]);

    // Listener for openChat event
    useEffect(() => {
        const handleOpenChat = () => setIsOpen(true);
        window.addEventListener('openChat', handleOpenChat);
        return () => window.removeEventListener('openChat', handleOpenChat);
    }, []);

    // Sequencing logic when chat opens for the first time
    useEffect(() => {
        if (isOpen && messages.length === 1 && !showFollowUp && !showTyping) {
            const typingTimer = setTimeout(() => {
                setShowTyping(true);
                const followUpTimer = setTimeout(() => {
                    setShowTyping(false);
                    setShowFollowUp(true);
                }, 2000);
                return () => clearTimeout(followUpTimer);
            }, 800);
            return () => clearTimeout(typingTimer);
        }
    }, [isOpen, messages.length]);

    const handleSend = async (e, customMessage = null) => {
        if (e) e.preventDefault();
        const userMessage = customMessage || input.trim();
        if (!userMessage || isLoading) return;

        setInput('');
        setShowFollowUp(false);
        setMessages((prev) => [
            ...prev,
            { id: uuidv4(), role: 'user', content: userMessage },
        ]);
        setIsLoading(true);

        try {
            const response = await axios.post(WEBHOOK_URL, {
                action: 'sendMessage',
                chatInput: userMessage,
                sessionId,
            });

            const aiResponse = typeof response.data === 'string'
                ? response.data
                : (response.data.output || response.data.text || response.data.message || response.data.response || "I'm sorry, I couldn't process that.");

            setMessages((prev) => [
                ...prev,
                { id: uuidv4(), role: 'assistant', content: aiResponse },
            ]);
        } catch (error) {
            console.error('Chat Error:', error);
            setMessages((prev) => [
                ...prev,
                { id: uuidv4(), role: 'assistant', content: "Sorry, I'm having trouble connecting to my brain right now. Please try again later." },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`chat-widget-container ${isOpen ? 'active' : ''}`}>
            {/* Notification Bubble */}
            <AnimatePresence>
                {showNotification && !isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.92 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.92 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                        style={{ transformOrigin: 'bottom right' }}
                        className="chat-notification-bubble"
                    >
                        <button
                            type="button"
                            className="notification-content"
                            onClick={() => setIsOpen(true)}
                        >
                            <span className="notification-avatar">
                                <img src={`${PUBLIC_URL}/images/me.jpg`} alt="" />
                                <span className="online-dot" />
                            </span>
                            <span className="notification-text">
                                <span className="notification-title">Ajith&apos;s AI assistant</span>
                                <span className="notification-message">Ask about my projects, skills or availability.</span>
                            </span>
                        </button>
                        <button
                            type="button"
                            className="close-notification"
                            aria-label="Dismiss"
                            onClick={() => { markGreetingSeen(); setShowNotification(false); }}
                        >
                            <X size={14} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.95, transformOrigin: 'bottom right' }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 40, scale: 0.95 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                        className="chat-window-v2"
                    >
                        {/* Brand Header */}
                        <div className="chat-header-v2">
                            <div className="header-flex">
                                <div className="specialist-info">
                                    <div className="avatar-container">
                                        <img
                                            src={`${PUBLIC_URL}/images/me.jpg`}
                                            alt="Ajith"
                                            className="specialist-img"
                                        />
                                        <span className="status-indicator"></span>
                                    </div>
                                    <div className="text-container">
                                        <div className="name-row">
                                            <h3>Ajith Joseph</h3>
                                        </div>
                                        <p className="role-text">AI Lead Engineer</p>
                                    </div>
                                </div>
                                <div
                                    onClick={() => setIsOpen(false)}
                                    className="header-close-btn"
                                    role="button"
                                    tabIndex={0}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                                        <path fill="none" d="M0 0h24v24H0z" />
                                        <path fill="currentColor" d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59l-4.89-4.88a1 1 0 00-1.41 1.41L10.59 12l-4.88 4.89a1 1 0 101.41 1.41L12 13.41l4.89 4.88a1 1 0 001.41-1.41L13.41 12l4.88-4.89a1 1 0 000-1.41z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Chat Body */}
                        <div className="chat-body-v2">
                            {messages.map((msg, index) => (
                                <motion.div
                                    key={msg.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`msg-wrapper ${msg.role}`}
                                >
                                    <div className="msg-content">
                                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                                    </div>
                                </motion.div>
                            ))}

                            {showTyping && (
                                <div className="typing-indicator-v2">
                                    <span></span><span></span><span></span>
                                </div>
                            )}

                            {showFollowUp && messages.length === 1 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="sequence-content"
                                >
                                    <div className="msg-wrapper assistant">
                                        <div className="msg-content">
                                            <p>I&apos;m here to help you navigate through my experience. What would you like to know?</p>
                                        </div>
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="quick-actions-list">
                                        {QUICK_ACTIONS.map((action) => (
                                            <button
                                                key={action.label}
                                                onClick={() => handleSend(null, action.topic)}
                                                className="quick-action-button"
                                            >
                                                <div className="action-label-group">
                                                    <action.icon size={16} className="action-icon" />
                                                    <span className="action-text">{action.label}</span>
                                                </div>
                                                <SendToBack size={12} className="action-arrow" />
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {isLoading && (
                                <div className="typing-indicator-v2">
                                    <span></span><span></span><span></span>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Footer */}
                        <div className="chat-footer-v2">
                            <div className="input-section">
                                {/* <p className="input-label">ASK ME ANYTHING...</p> */}
                                <form onSubmit={handleSend} className="input-form">
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder="Type your query..."
                                        disabled={isLoading}
                                    />
                                    <div
                                        className="send-action-btn"
                                        onClick={(e) => handleSend(e)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <SendHorizontal size={20} />
                                    </div>
                                </form>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="chat-toggle-main"
            >
                <AnimatePresence mode="wait">
                    {isOpen ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                        >
                            <X size={28} />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="open"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                        >
                            <MessagesSquare size={28} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </button>
        </div>
    );
};

export default ChatWidget;
